import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";

// Configure axios with credentials for authentication
const axiosWithAuth = axios.create({
  withCredentials: true,
});
import {
  X,
  AlertTriangle,
  CheckCircle2,
  Shield,
  Clock,
  Wrench,
  HardHat,
  Target,
  Radio,
  MapPin,
  Phone,
  User,
  Calendar,
  Clipboard,
  FileText,
  Send,
  Link2,
  Mail,
  MessageSquare,
  Loader2,
  ChevronDown,
  ChevronUp,
  Truck,
  Eye,
  Edit3,
  Plus,
  Trash2,
  ArrowLeft,
  Download,
  Copy,
  Check
} from "lucide-react";

const fmt = (n) => `$${(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const OperatorOrderBuilder = ({
  estimateId,
  estimateData,
  clientInfo,
  companySettings,
  onClose
}) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sendModalOpen, setSendModalOpen] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [expandedSections, setExpandedSections] = useState({});
  const [publicToken, setPublicToken] = useState(null);
  
  // Editable state for all sections
  const [missionId, setMissionId] = useState("");
  const [priority, setPriority] = useState("STANDARD");
  const [status, setStatus] = useState("READY FOR DEPLOYMENT");
  const [siteAccess, setSiteAccess] = useState("7:00 AM - 6:00 PM");
  const [staging, setStaging] = useState("As directed by client");
  
  // Pre-mission checklist (editable)
  const [preMissionChecklist, setPreMissionChecklist] = useState([
    { id: 1, text: "All materials verified on-site", checked: false },
    { id: 2, text: "Tools and equipment checked", checked: false },
    { id: 3, text: "PPE available for all crew", checked: false },
    { id: 4, text: "Client notified of arrival", checked: false },
    { id: 5, text: "Work zone established", checked: false },
    { id: 6, text: "Pre-existing damage documented", checked: false }
  ]);
  
  // Post-mission checklist (editable)
  const [postMissionChecklist, setPostMissionChecklist] = useState([
    { id: 1, text: "All work areas cleaned", checked: false },
    { id: 2, text: "Tools and equipment secured", checked: false },
    { id: 3, text: "Debris properly disposed", checked: false },
    { id: 4, text: "Progress photos uploaded", checked: false },
    { id: 5, text: "Daily report submitted", checked: false },
    { id: 6, text: "Client signed off on completed work", checked: false }
  ]);
  
  // Trade sections with editable data
  const [tradeSections, setTradeSections] = useState([]);
  
  // Edit modal state
  const [editModal, setEditModal] = useState({ open: false, type: null, data: null, index: null, subIndex: null });
  
  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  };

  // Get groups from estimate data
  const groupsFromProps = estimateData?.groups || estimateData?.allScopes?.base?.groups || [];
  
  // State for groups fetched from backend (fallback when props are stale)
  const [fetchedGroups, setFetchedGroups] = useState([]);
  
  // Use fetched groups if props are empty but we have an estimateId
  const groups = groupsFromProps.length > 0 ? groupsFromProps : fetchedGroups;
  
  // API base URL
  const API_BASE = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : "/api";
  
  // Fetch estimate data from backend when props are empty (fixes stale state issue)
  useEffect(() => {
    const fetchEstimateData = async () => {
      // Only fetch if we have an estimateId but props are empty
      if (estimateId && groupsFromProps.length === 0) {
        console.log("[OperatorOrderBuilder] Props are empty, fetching from backend...");
        try {
          const response = await axiosWithAuth.get(`${API_BASE}/estimates/${estimateId}/frontend-scopes`);
          const scopes = response.data?.scopes;
          if (scopes?.base?.groups && scopes.base.groups.length > 0) {
            console.log("[OperatorOrderBuilder] Fetched groups from backend:", scopes.base.groups.length);
            setFetchedGroups(scopes.base.groups);
          }
        } catch (error) {
          console.warn("[OperatorOrderBuilder] Could not fetch estimate data:", error);
        }
      }
    };
    
    fetchEstimateData();
  }, [estimateId, groupsFromProps.length, API_BASE]);
  
  // Load or create operator order
  useEffect(() => {
    const loadOrCreateOrder = async () => {
      // CRITICAL FIX: Allow rendering with live data even without estimateId
      const hasLiveData = groups && groups.length > 0;
      
      if (!estimateId && !hasLiveData) {
        setLoading(false);
        return;
      }
      
      if (!estimateId && hasLiveData) {
        // No ID but we have live data - use it directly
        console.log("[OperatorOrderBuilder] Using live data (no estimateId)");
        setLoading(false);
        return;
      }
      
      try {
        // Try to load existing order
        const response = await axiosWithAuth.get(`${API_BASE}/operator-orders/${estimateId}`);
        
        if (response.data && response.data.public_token) {
          setPublicToken(response.data.public_token);
          // Load saved data
          if (response.data.priority) setPriority(response.data.priority);
          if (response.data.status) setStatus(response.data.status);
          if (response.data.site_access) setSiteAccess(response.data.site_access);
          if (response.data.staging) setStaging(response.data.staging);
          if (response.data.pre_mission_checklist?.length > 0) setPreMissionChecklist(response.data.pre_mission_checklist);
          if (response.data.post_mission_checklist?.length > 0) setPostMissionChecklist(response.data.post_mission_checklist);
          if (response.data.trade_sections?.length > 0) setTradeSections(response.data.trade_sections);
          setLoading(false);
          return;
        }
      } catch (error) {
        // Order doesn't exist - that's OK, we'll use live data
        console.log("[OperatorOrderBuilder] No saved order, using live data");
      }
      
      // Only try to create if we have an estimateId
      if (estimateId) {
        try {
          // Create new order
          const createResponse = await axiosWithAuth.post(`${API_BASE}/operator-orders`, {
            estimate_id: estimateId,
            project_name: clientInfo?.project_name || estimateData?.job_name || "Project",
            site_address: clientInfo?.address || "",
            site_contact: clientInfo?.name || "",
            company_name: companySettings?.company_name || ""
          });
          
          if (createResponse.data?.public_token) {
            setPublicToken(createResponse.data.public_token);
          }
        } catch (error) {
          console.warn("[OperatorOrderBuilder] Failed to create order, using live data:", error);
        }
      }
      
      setLoading(false);
    };
    
    loadOrCreateOrder();
  }, [estimateId, API_BASE, clientInfo, companySettings, estimateData, groups]);

  // ═══════════════════════════════════════════════════════════════
  // FETCH CHATGPT OPERATOR ORDER DATA
  // Updates phase durations and crew info - keeps checklists static
  // ═══════════════════════════════════════════════════════════════
  const [missionBriefData, setMissionBriefData] = useState(null);
  
  useEffect(() => {
    const fetchChatGPTOperatorData = async () => {
      if (!estimateId) return;
      
      try {
        const response = await axiosWithAuth.get(`${API_BASE}/estimates/${estimateId}`);
        const missionBrief = response.data?.mission_brief_documents;
        
        if (missionBrief?.operator_order) {
          // Store the full operator_order data for EST. DURATION display
          setMissionBriefData(missionBrief.operator_order);
          
          const chatgptPhases = missionBrief.operator_order.phases || missionBrief.operator_order.weekly_schedule || [];
          
          // Merge ChatGPT phase data with existing trade sections
          // This adds week_range and duration info without replacing structure
          setTradeSections(prev => {
            if (prev.length === 0) {
              // No existing sections - create from ChatGPT
              return chatgptPhases.map((phase, idx) => ({
                id: idx + 1,
                name: phase.phase_name || phase.week_title,
                week_range: phase.week_range || `Week ${phase.week_number}`,
                duration_days: phase.duration_days,
                crew: phase.crew_requirements || [],
                tasks: phase.daily_objectives || [],
                items: (phase.daily_objectives || []).map((task, taskIdx) => ({
                  id: taskIdx,
                  name: task,
                  quantity: 1,
                  unit: "EA",
                  status: "PENDING"
                })),
                criticalSteps: phase.quality_checkpoints || [],
                requiredTools: [],
                safetyProtocols: phase.safety_protocols || [],
                qcChecklist: phase.inspections_required || []
              }));
            }
            
            // Existing sections - merge duration data from ChatGPT
            return prev.map((section, idx) => {
              const chatgptPhase = chatgptPhases[idx];
              if (chatgptPhase) {
                return {
                  ...section,
                  week_range: chatgptPhase.week_range || chatgptPhase.week_number ? `Week ${chatgptPhase.week_number}` : section.week_range,
                  duration_days: chatgptPhase.duration_days || section.duration_days,
                  crew: chatgptPhase.crew_requirements || section.crew || [],
                };
              }
              return section;
            });
          });
          
          console.log("[OperatorOrderBuilder] Updated phases from ChatGPT");
        }
        
      } catch (error) {
        console.log("[OperatorOrderBuilder] No ChatGPT data, using defaults");
      }
    };
    
    fetchChatGPTOperatorData();
  }, [estimateId, API_BASE]);

  // Save order data
  const saveOrder = useCallback(async () => {
    if (!estimateId) {
      toast.error("Save estimate first to enable saving documents");
      return;
    }
    
    setSaving(true);
    try {
      await axiosWithAuth.post(`${API_BASE}/operator-orders`, {
        estimate_id: estimateId,
        priority,
        status,
        site_access: siteAccess,
        staging,
        pre_mission_checklist: preMissionChecklist,
        post_mission_checklist: postMissionChecklist,
        trade_sections: tradeSections,
        project_name: clientInfo?.project_name || estimateData?.job_name,
        site_address: clientInfo?.address || "",
        site_contact: clientInfo?.name || "",
        company_name: companySettings?.company_name || ""
      });
      toast.success("Order saved");
    } catch (error) {
      console.error("Failed to save order:", error);
      toast.error("Failed to save");
    } finally {
      setSaving(false);
    }
  }, [estimateId, API_BASE, priority, status, siteAccess, staging, preMissionChecklist, postMissionChecklist, tradeSections, clientInfo, companySettings, estimateData]);

  // Initialize trade sections from estimate groups
  useEffect(() => {
    if (groups.length > 0 && tradeSections.length === 0) {
      const sections = groups.map((group, idx) => {
        const tradeData = getDefaultTradeData(group.name);
        return {
          id: idx,
          name: group.name,
          items: (group.items || []).map((item, itemIdx) => ({
            id: itemIdx,
            name: item.itemName || item.name || "Task",
            quantity: item.quantity || 1,
            unit: item.unit || "EA",
            status: "PENDING"
          })),
          criticalSteps: tradeData.criticalSteps,
          requiredTools: tradeData.requiredTools,
          safetyProtocols: tradeData.safetyProtocols,
          qcChecklist: tradeData.qcChecklist
        };
      });
      setTradeSections(sections);
    }
    
    // Set mission ID
    setMissionId(`OP-${(estimateId || "00001").toUpperCase().slice(-8)}`);
  }, [groups, estimateId]);

  // Get default trade data based on group name
  const getDefaultTradeData = (groupName) => {
    const upperName = (groupName || "").toUpperCase();
    
    const tradeData = {
      DECK: {
        criticalSteps: [
          "Verify footer locations per plan - check property setbacks",
          "Inspect all lumber for defects before installation",
          "Ensure proper joist spacing (16\" OC standard)",
          "Pre-drill all fastener holes to prevent splitting",
          "Check level every 4 feet during installation"
        ],
        requiredTools: ["Circular saw", "Drill/impact driver", "Level (4ft)", "Speed square", "Post level", "Chalk line", "Tape measure", "Safety glasses"],
        safetyProtocols: [
          "Hard hat required when working below overhead activity",
          "Safety glasses during all cutting operations",
          "Steel-toe boots mandatory on site",
          "Establish fall protection above 6 feet"
        ],
        qcChecklist: [
          { text: "Posts plumb within 1/8\" over 8 feet", checked: false },
          { text: "Decking gaps uniform (1/8\" for pressure treated)", checked: false },
          { text: "Railing height meets code (36\" residential / 42\" commercial)", checked: false },
          { text: "All fasteners properly countersunk", checked: false },
          { text: "No visible defects in finished decking", checked: false }
        ]
      },
      PLUMBING: {
        criticalSteps: [
          "Shut off water main and verify complete isolation",
          "Document existing conditions with photos",
          "Verify drain slope minimum 1/4\" per foot",
          "Pressure test all connections before cover",
          "Flush system completely before client handoff"
        ],
        requiredTools: ["Pipe wrench set", "PEX crimper", "Torch kit", "Level", "Thread tape", "Teflon paste", "Tubing cutter", "Basin wrench"],
        safetyProtocols: [
          "PPE required: Safety glasses, work gloves",
          "Verify no live electrical near water work",
          "Test for gas leaks if working near gas lines",
          "Proper ventilation when soldering"
        ],
        qcChecklist: [
          { text: "No visible leaks under pressure test", checked: false },
          { text: "Proper support every 32\" on horizontal runs", checked: false },
          { text: "P-trap water seal intact", checked: false },
          { text: "Hot/cold properly marked", checked: false },
          { text: "Shut-off valves accessible", checked: false }
        ]
      },
      ELECTRICAL: {
        criticalSteps: [
          "KILL POWER at main breaker - verify with meter",
          "Test all wires with multimeter BEFORE touching",
          "Follow NEC code for proper wire gauge",
          "Label all circuits in panel",
          "Document all changes with photos"
        ],
        requiredTools: ["Multimeter", "Wire strippers", "Fish tape", "Non-contact voltage tester", "Conduit bender", "Wire nuts", "Electrical tape", "Label maker"],
        safetyProtocols: [
          "⚡ LOCK OUT / TAG OUT REQUIRED - NO EXCEPTIONS",
          "NEVER work on live circuits",
          "Ground all metal junction boxes",
          "Use rated PPE for voltage level",
          "Fire extinguisher within 25 feet"
        ],
        qcChecklist: [
          { text: "All connections tight - no exposed copper", checked: false },
          { text: "GFCI protection where required", checked: false },
          { text: "Proper box fill capacity", checked: false },
          { text: "Circuit breaker properly rated", checked: false },
          { text: "Final test: all outlets energized correctly", checked: false }
        ]
      },
      DEFAULT: {
        criticalSteps: [
          "Review complete scope before starting",
          "Verify all material quantities on-site",
          "Document pre-existing conditions with photos",
          "Establish work zone boundaries",
          "Confirm client contact availability"
        ],
        requiredTools: ["Basic hand tools", "Measuring tape (25ft)", "Level (4ft)", "Pencil/marker", "Utility knife", "Speed square", "Drill/driver"],
        safetyProtocols: [
          "PPE appropriate for task",
          "Keep work area clean and organized",
          "Report hazards immediately",
          "No shortcuts on safety"
        ],
        qcChecklist: [
          { text: "Work matches approved scope", checked: false },
          { text: "Area cleaned of debris", checked: false },
          { text: "Client walkthrough completed", checked: false },
          { text: "Photo documentation complete", checked: false },
          { text: "Punch list items addressed", checked: false }
        ]
      }
    };
    
    if (upperName.includes("DECK") || upperName.includes("FRAMING") || upperName.includes("JOIST") || upperName.includes("STAIR") || upperName.includes("RAILING") || upperName.includes("POST") || upperName.includes("FOOTER")) return tradeData.DECK;
    if (upperName.includes("PLUMB") || upperName.includes("PIPE") || upperName.includes("DRAIN")) return tradeData.PLUMBING;
    if (upperName.includes("ELEC") || upperName.includes("WIRE") || upperName.includes("OUTLET")) return tradeData.ELECTRICAL;
    return tradeData.DEFAULT;
  };

  // Calculate totals
  const totalTasks = tradeSections.reduce((sum, section) => sum + section.items.length, 0);

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: prev[sectionId] === false ? true : false
    }));
  };

  // Edit handlers
  const handleEditCriticalStep = (sectionIdx, stepIdx, newValue) => {
    setTradeSections(prev => prev.map((section, i) => {
      if (i === sectionIdx) {
        const newSteps = [...section.criticalSteps];
        newSteps[stepIdx] = newValue;
        return { ...section, criticalSteps: newSteps };
      }
      return section;
    }));
  };

  const handleAddCriticalStep = (sectionIdx) => {
    setTradeSections(prev => prev.map((section, i) => {
      if (i === sectionIdx) {
        return { ...section, criticalSteps: [...section.criticalSteps, "New critical step"] };
      }
      return section;
    }));
  };

  const handleDeleteCriticalStep = (sectionIdx, stepIdx) => {
    setTradeSections(prev => prev.map((section, i) => {
      if (i === sectionIdx) {
        return { ...section, criticalSteps: section.criticalSteps.filter((_, idx) => idx !== stepIdx) };
      }
      return section;
    }));
  };

  const handleEditTool = (sectionIdx, toolIdx, newValue) => {
    setTradeSections(prev => prev.map((section, i) => {
      if (i === sectionIdx) {
        const newTools = [...section.requiredTools];
        newTools[toolIdx] = newValue;
        return { ...section, requiredTools: newTools };
      }
      return section;
    }));
  };

  const handleAddTool = (sectionIdx) => {
    setTradeSections(prev => prev.map((section, i) => {
      if (i === sectionIdx) {
        return { ...section, requiredTools: [...section.requiredTools, "New tool"] };
      }
      return section;
    }));
  };

  const handleDeleteTool = (sectionIdx, toolIdx) => {
    setTradeSections(prev => prev.map((section, i) => {
      if (i === sectionIdx) {
        return { ...section, requiredTools: section.requiredTools.filter((_, idx) => idx !== toolIdx) };
      }
      return section;
    }));
  };

  const handleEditSafetyProtocol = (sectionIdx, protocolIdx, newValue) => {
    setTradeSections(prev => prev.map((section, i) => {
      if (i === sectionIdx) {
        const newProtocols = [...section.safetyProtocols];
        newProtocols[protocolIdx] = newValue;
        return { ...section, safetyProtocols: newProtocols };
      }
      return section;
    }));
  };

  const handleAddSafetyProtocol = (sectionIdx) => {
    setTradeSections(prev => prev.map((section, i) => {
      if (i === sectionIdx) {
        return { ...section, safetyProtocols: [...section.safetyProtocols, "New safety protocol"] };
      }
      return section;
    }));
  };

  const handleDeleteSafetyProtocol = (sectionIdx, protocolIdx) => {
    setTradeSections(prev => prev.map((section, i) => {
      if (i === sectionIdx) {
        return { ...section, safetyProtocols: section.safetyProtocols.filter((_, idx) => idx !== protocolIdx) };
      }
      return section;
    }));
  };

  const handleEditQcItem = (sectionIdx, itemIdx, newText) => {
    setTradeSections(prev => prev.map((section, i) => {
      if (i === sectionIdx) {
        const newQc = [...section.qcChecklist];
        newQc[itemIdx] = { ...newQc[itemIdx], text: newText };
        return { ...section, qcChecklist: newQc };
      }
      return section;
    }));
  };

  const handleAddQcItem = (sectionIdx) => {
    setTradeSections(prev => prev.map((section, i) => {
      if (i === sectionIdx) {
        return { ...section, qcChecklist: [...section.qcChecklist, { text: "New QC item", checked: false }] };
      }
      return section;
    }));
  };

  const handleDeleteQcItem = (sectionIdx, itemIdx) => {
    setTradeSections(prev => prev.map((section, i) => {
      if (i === sectionIdx) {
        return { ...section, qcChecklist: section.qcChecklist.filter((_, idx) => idx !== itemIdx) };
      }
      return section;
    }));
  };

  const handleEditChecklistItem = (type, itemIdx, newText) => {
    if (type === 'pre') {
      setPreMissionChecklist(prev => prev.map((item, i) => 
        i === itemIdx ? { ...item, text: newText } : item
      ));
    } else {
      setPostMissionChecklist(prev => prev.map((item, i) => 
        i === itemIdx ? { ...item, text: newText } : item
      ));
    }
  };

  const handleAddChecklistItem = (type) => {
    const newItem = { id: Date.now(), text: "New checklist item", checked: false };
    if (type === 'pre') {
      setPreMissionChecklist(prev => [...prev, newItem]);
    } else {
      setPostMissionChecklist(prev => [...prev, newItem]);
    }
  };

  const handleDeleteChecklistItem = (type, itemIdx) => {
    if (type === 'pre') {
      setPreMissionChecklist(prev => prev.filter((_, i) => i !== itemIdx));
    } else {
      setPostMissionChecklist(prev => prev.filter((_, i) => i !== itemIdx));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: "#020617" }}>
        <div className="text-blue-400 text-lg">Loading mission brief...</div>
      </div>
    );
  }

  // Check if we have data to work with (either estimateId OR groups with data)
  const hasEstimateData = estimateId || (groups && groups.length > 0);
  
  // EMPTY STATE: No estimate data available
  if (!hasEstimateData) {
    return (
      <div className="min-h-screen pt-14" style={{ backgroundColor: "#020617" }}>
        {/* Header Bar - Fixed at top */}
        <div 
          className="fixed top-0 left-0 right-0 z-[200] px-6 py-3 flex items-center justify-between"
          style={{ 
            backgroundColor: "#0f172a",
            borderBottom: "2px solid rgba(59, 130, 246, 0.5)",
            boxShadow: "0 4px 20px rgba(0, 0, 0, 0.5)"
          }}
        >
          <button
            onClick={onClose}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-800 text-white hover:bg-slate-700 transition-all font-medium"
          >
            <ArrowLeft className="h-4 w-4" />
            <span>← Exit</span>
          </button>
          
          <div className="flex items-center gap-3">
            <span className="text-blue-400 font-bold">OPERATOR ORDER</span>
          </div>
          
          <button
            onClick={onClose}
            className="p-2 rounded-lg bg-red-500/20 text-red-400 hover:bg-red-500/30 transition-all"
            title="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex flex-col items-center justify-center" style={{ minHeight: "calc(100vh - 60px)" }}>
          <div className="text-center p-8 rounded-lg" style={{ backgroundColor: "#0f172a", border: "2px dashed #334155", maxWidth: "500px" }}>
            <h2 className="text-xl font-bold text-white mb-4">No Estimate Data</h2>
            <p className="text-slate-400 mb-6">Create an estimate first using the ESTIMATING COMMAND, then return here to build your Operator Order.</p>
            <button
              onClick={onClose}
              className="px-6 py-3 rounded font-bold text-white transition-all hover:scale-105"
              style={{ backgroundColor: "#3b82f6" }}
            >
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen overflow-y-auto pb-32 pt-14"
      style={{ 
        backgroundColor: "#020617",
        fontFamily: "'Inter', sans-serif"
      }}
    >
      {/* Fixed Header Bar - Always visible at top */}
      <div 
        className="fixed top-0 left-0 right-0 z-[200] px-6 py-3 flex items-center justify-between"
        style={{ 
          backgroundColor: "#0f172a",
          borderBottom: "2px solid rgba(59, 130, 246, 0.5)",
          boxShadow: "0 4px 20px rgba(0, 0, 0, 0.5)"
        }}
      >
        <button
          onClick={onClose}
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-800 text-white hover:bg-slate-700 transition-all font-medium"
        >
          <ArrowLeft className="h-4 w-4" />
          <span>← Exit</span>
        </button>
        
        <div className="flex items-center gap-3">
          <span className="text-blue-400 font-bold">OPERATOR ORDER</span>
          <span className="text-slate-500">|</span>
          <span className="text-slate-400">{clientInfo?.project_name || "Field Operation"}</span>
        </div>
        
        <button
          onClick={onClose}
          className="p-2 rounded-lg bg-red-500/20 text-red-400 hover:bg-red-500/30 transition-all"
          title="Close"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      {/* Document Container */}
      <div 
        className="mx-auto mt-8"
        style={{ 
          maxWidth: "900px",
          backgroundColor: "#0f172a",
          borderRadius: "8px",
          border: "1px solid rgba(59, 130, 246, 0.3)",
          boxShadow: "0 0 40px rgba(0, 0, 0, 0.5)"
        }}
      >
        {/* ═══════════════════════════════════════════════════════════════════════════
            HEADER - Tactical Mission Header
            ═══════════════════════════════════════════════════════════════════════════ */}
        <div 
          className="px-10 pt-8 pb-6"
          style={{
            background: "linear-gradient(180deg, rgba(59, 130, 246, 0.15) 0%, transparent 100%)",
            borderBottom: "1px solid rgba(59, 130, 246, 0.3)",
            borderRadius: "8px 8px 0 0"
          }}
        >
          {/* Company Info */}
          <div className="mb-6">
            <h1 
              className="text-2xl font-bold tracking-wide"
              style={{ 
                color: "#3b82f6",
                fontFamily: "'Playfair Display', serif"
              }}
            >
              {companySettings?.company_name || "Alpha Construction Pros"}
            </h1>
            <p className="text-slate-400 text-sm mt-1">
              {companySettings?.address || "123 Builder Lane, Construction City, ST 12345"}
            </p>
          </div>
          
          {/* Mission Metadata - Editable */}
          <div className="flex items-center gap-8 flex-wrap">
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-widest">MISSION ID</p>
              <input
                type="text"
                value={missionId}
                onChange={(e) => setMissionId(e.target.value)}
                className="text-white font-bold text-sm bg-transparent border-b border-transparent hover:border-blue-500 focus:border-blue-500 outline-none"
                style={{ fontFamily: "'JetBrains Mono', monospace" }}
              />
            </div>
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-widest">ISSUE DATE</p>
              <p className="text-white font-bold text-sm">{formatDate()}</p>
            </div>
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-widest">PRIORITY</p>
              <select
                value={priority}
                onChange={(e) => setPriority(e.target.value)}
                className="font-bold text-sm px-2 py-0.5 rounded bg-transparent border-none outline-none cursor-pointer"
                style={{ 
                  backgroundColor: priority === "URGENT" ? "rgba(239, 68, 68, 0.2)" : "rgba(245, 158, 11, 0.2)", 
                  color: priority === "URGENT" ? "#ef4444" : "#f59e0b" 
                }}
              >
                <option value="STANDARD">STANDARD</option>
                <option value="HIGH">HIGH</option>
                <option value="URGENT">URGENT</option>
              </select>
            </div>
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-widest">STATUS</p>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className="font-bold text-sm px-2 py-0.5 rounded bg-transparent border-none outline-none cursor-pointer"
                style={{ backgroundColor: "rgba(16, 185, 129, 0.2)", color: "#10b981" }}
              >
                <option value="READY FOR DEPLOYMENT">READY FOR DEPLOYMENT</option>
                <option value="IN PROGRESS">IN PROGRESS</option>
                <option value="COMPLETED">COMPLETED</option>
              </select>
            </div>
          </div>
        </div>

        {/* ═══════════════════════════════════════════════════════════════════════════
            SECTION 2: MISSION TITLE & STATS
            ═══════════════════════════════════════════════════════════════════════════ */}
        <div className="px-10 py-8">
          <div className="flex items-center gap-3 mb-2">
            <Target className="h-5 w-5 text-blue-400" />
            <p className="text-xs uppercase tracking-[4px] text-blue-400">OPERATOR ORDER</p>
          </div>
          <h2 
            className="text-3xl font-bold text-white mb-4"
            style={{ fontFamily: "'Playfair Display', serif", lineHeight: 1.2 }}
          >
            {clientInfo?.project_name || estimateData?.job_name || "Field Operation Brief"}
          </h2>
          
          {/* Quick Stats */}
          <div className="grid grid-cols-4 gap-4 mb-8">
            <div 
              className="p-4 rounded-lg text-center"
              style={{ backgroundColor: "rgba(59, 130, 246, 0.1)", border: "1px solid rgba(59, 130, 246, 0.3)" }}
            >
              <p className="text-2xl font-bold text-blue-400">{tradeSections.length}</p>
              <p className="text-xs text-slate-500 uppercase">Trade Sections</p>
            </div>
            <div 
              className="p-4 rounded-lg text-center"
              style={{ backgroundColor: "rgba(16, 185, 129, 0.1)", border: "1px solid rgba(16, 185, 129, 0.3)" }}
            >
              <p className="text-2xl font-bold text-emerald-400">{totalTasks}</p>
              <p className="text-xs text-slate-500 uppercase">Task Items</p>
            </div>
            <div 
              className="p-4 rounded-lg text-center"
              style={{ backgroundColor: "rgba(245, 158, 11, 0.1)", border: "1px solid rgba(245, 158, 11, 0.3)" }}
            >
              <p className="text-2xl font-bold text-amber-400">
                {tradeSections[0]?.week_range ? 
                  `${(missionBriefData?.total_weeks || missionBriefData?.working_weeks || 12)} wk` : 
                  'TBD'}
              </p>
              <p className="text-xs text-slate-500 uppercase">Est. Duration</p>
            </div>
            <div 
              className="p-4 rounded-lg text-center"
              style={{ backgroundColor: "rgba(239, 68, 68, 0.1)", border: "1px solid rgba(239, 68, 68, 0.3)" }}
            >
              <p className="text-2xl font-bold text-red-400">{tradeSections.length}</p>
              <p className="text-xs text-slate-500 uppercase">Safety Briefs</p>
            </div>
          </div>
        </div>

        {/* ═══════════════════════════════════════════════════════════════════════════
            SECTION 3: JOB SITE INTELLIGENCE - Editable
            ═══════════════════════════════════════════════════════════════════════════ */}
        <div className="px-10 pb-8">
          <div className="flex items-center gap-2 mb-4">
            <Radio className="h-4 w-4 text-blue-400" />
            <p className="text-xs text-slate-500 uppercase tracking-widest">JOB SITE INTELLIGENCE</p>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            {/* Client Info */}
            <div 
              className="p-5 rounded-lg"
              style={{ backgroundColor: "rgba(30, 41, 59, 0.5)", border: "1px solid rgba(100, 116, 139, 0.2)" }}
            >
              <p className="text-xs text-slate-500 uppercase tracking-widest mb-3">CLIENT CONTACT</p>
              <div className="space-y-2">
                <div className="flex items-center gap-3">
                  <User className="h-4 w-4 text-slate-500" />
                  <span className="text-white font-medium">{clientInfo?.name || "Client Name"}</span>
                </div>
                <div className="flex items-center gap-3">
                  <Phone className="h-4 w-4 text-slate-500" />
                  <span className="text-slate-400">{clientInfo?.phone || "No phone on file"}</span>
                </div>
                <div className="flex items-center gap-3">
                  <Mail className="h-4 w-4 text-slate-500" />
                  <span className="text-slate-400">{clientInfo?.email || "No email on file"}</span>
                </div>
              </div>
            </div>
            
            {/* Site Location - Editable */}
            <div 
              className="p-5 rounded-lg"
              style={{ backgroundColor: "rgba(30, 41, 59, 0.5)", border: "1px solid rgba(100, 116, 139, 0.2)" }}
            >
              <p className="text-xs text-slate-500 uppercase tracking-widest mb-3">JOB SITE LOCATION</p>
              <div className="space-y-2">
                <div className="flex items-start gap-3">
                  <MapPin className="h-4 w-4 text-slate-500 mt-0.5" />
                  <span className="text-white font-medium">{clientInfo?.address || "Address TBD"}</span>
                </div>
                <div className="flex items-center gap-3">
                  <Clock className="h-4 w-4 text-slate-500" />
                  <input
                    type="text"
                    value={siteAccess}
                    onChange={(e) => setSiteAccess(e.target.value)}
                    className="text-slate-400 bg-transparent border-b border-transparent hover:border-blue-500 focus:border-blue-500 outline-none flex-1"
                    placeholder="Site Access Hours"
                  />
                </div>
                <div className="flex items-center gap-3">
                  <Truck className="h-4 w-4 text-slate-500" />
                  <input
                    type="text"
                    value={staging}
                    onChange={(e) => setStaging(e.target.value)}
                    className="text-slate-400 bg-transparent border-b border-transparent hover:border-blue-500 focus:border-blue-500 outline-none flex-1"
                    placeholder="Staging Location"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ═══════════════════════════════════════════════════════════════════════════
            SECTION 4: PRE-MISSION CHECKLIST - Editable
            ═══════════════════════════════════════════════════════════════════════════ */}
        <div className="px-10 pb-8">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Clipboard className="h-4 w-4 text-amber-400" />
              <p className="text-xs text-slate-500 uppercase tracking-widest">PRE-MISSION CHECKLIST</p>
            </div>
            <button
              onClick={() => handleAddChecklistItem('pre')}
              className="flex items-center gap-1 text-amber-400 hover:text-amber-300 text-sm"
            >
              <Plus className="h-4 w-4" />
              Add Item
            </button>
          </div>
          
          <div 
            className="p-5 rounded-lg"
            style={{ backgroundColor: "rgba(245, 158, 11, 0.1)", border: "1px solid rgba(245, 158, 11, 0.3)" }}
          >
            <div className="grid grid-cols-2 gap-3">
              {preMissionChecklist.map((item, idx) => (
                <div key={item.id} className="flex items-center gap-3 group">
                  <div className="w-5 h-5 rounded border-2 border-amber-500/50 flex items-center justify-center flex-shrink-0">
                    <span className="text-xs text-amber-400">☐</span>
                  </div>
                  <input
                    type="text"
                    value={item.text}
                    onChange={(e) => handleEditChecklistItem('pre', idx, e.target.value)}
                    className="text-slate-300 bg-transparent border-b border-transparent hover:border-amber-500 focus:border-amber-500 outline-none flex-1"
                  />
                  <button
                    onClick={() => handleDeleteChecklistItem('pre', idx)}
                    className="opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-300"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ═══════════════════════════════════════════════════════════════════════════
            SECTION 5: TRADE SECTIONS - Fully Editable
            ═══════════════════════════════════════════════════════════════════════════ */}
        <div className="px-10 pb-8">
          <div className="flex items-center gap-2 mb-4">
            <Wrench className="h-4 w-4 text-blue-400" />
            <p className="text-xs text-slate-500 uppercase tracking-widest">MISSION EXECUTION BY TRADE</p>
          </div>
          
          <div className="space-y-4">
            {tradeSections.map((section, sectionIdx) => {
              const isExpanded = expandedSections[`trade-${sectionIdx}`] !== false;
              
              return (
                <div 
                  key={section.id}
                  className="rounded-lg overflow-hidden"
                  style={{ border: "1px solid rgba(59, 130, 246, 0.3)" }}
                >
                  {/* Section Header */}
                  <button
                    onClick={() => toggleSection(`trade-${sectionIdx}`)}
                    className="w-full px-5 py-4 flex items-center justify-between"
                    style={{ backgroundColor: "rgba(59, 130, 246, 0.15)" }}
                  >
                    <div className="flex items-center gap-3">
                      <HardHat className="h-5 w-5 text-blue-400" />
                      <span className="text-white font-bold text-lg">{section.name}</span>
                      <span className="text-slate-500 text-sm">({section.items.length} tasks)</span>
                      {/* ChatGPT Week Range Badge */}
                      {section.week_range && (
                        <span 
                          className="px-2 py-0.5 rounded text-xs font-bold"
                          style={{ backgroundColor: "rgba(16, 185, 129, 0.2)", color: "#10b981" }}
                        >
                          {section.week_range}
                        </span>
                      )}
                    </div>
                    {isExpanded ? <ChevronUp className="h-5 w-5 text-slate-400" /> : <ChevronDown className="h-5 w-5 text-slate-400" />}
                  </button>
                  
                  {isExpanded && (
                    <div style={{ backgroundColor: "rgba(15, 23, 42, 0.5)" }}>
                      {/* Critical Execution Steps - Editable */}
                      <div className="p-5 border-b border-slate-700/50">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <AlertTriangle className="h-4 w-4 text-red-400" />
                            <p className="text-xs text-red-400 uppercase tracking-wider font-bold">CRITICAL EXECUTION STEPS</p>
                          </div>
                          <button
                            onClick={() => handleAddCriticalStep(sectionIdx)}
                            className="flex items-center gap-1 text-red-400 hover:text-red-300 text-sm"
                          >
                            <Plus className="h-4 w-4" />
                            Add Step
                          </button>
                        </div>
                        <ol className="space-y-2">
                          {section.criticalSteps.map((step, stepIdx) => (
                            <li key={stepIdx} className="flex items-start gap-3 group">
                              <span 
                                className="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold mt-0.5"
                                style={{ backgroundColor: "rgba(239, 68, 68, 0.2)", color: "#f87171" }}
                              >
                                {stepIdx + 1}
                              </span>
                              <input
                                type="text"
                                value={step}
                                onChange={(e) => handleEditCriticalStep(sectionIdx, stepIdx, e.target.value)}
                                className="text-slate-300 bg-transparent border-b border-transparent hover:border-red-500 focus:border-red-500 outline-none flex-1"
                              />
                              <button
                                onClick={() => handleDeleteCriticalStep(sectionIdx, stepIdx)}
                                className="opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-300"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </li>
                          ))}
                        </ol>
                      </div>
                      
                      {/* Required Tools - Editable */}
                      <div className="p-5 border-b border-slate-700/50">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <Wrench className="h-4 w-4 text-amber-400" />
                            <p className="text-xs text-amber-400 uppercase tracking-wider font-bold">REQUIRED TOOLS & EQUIPMENT</p>
                          </div>
                          <button
                            onClick={() => handleAddTool(sectionIdx)}
                            className="flex items-center gap-1 text-amber-400 hover:text-amber-300 text-sm"
                          >
                            <Plus className="h-4 w-4" />
                            Add Tool
                          </button>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {section.requiredTools.map((tool, toolIdx) => (
                            <div 
                              key={toolIdx}
                              className="group flex items-center gap-1 px-3 py-1 rounded text-sm"
                              style={{ backgroundColor: "rgba(245, 158, 11, 0.15)", border: "1px solid rgba(245, 158, 11, 0.3)" }}
                            >
                              <input
                                type="text"
                                value={tool}
                                onChange={(e) => handleEditTool(sectionIdx, toolIdx, e.target.value)}
                                className="text-amber-300 bg-transparent border-none outline-none w-auto"
                                style={{ width: `${Math.max(tool.length * 8, 60)}px` }}
                              />
                              <button
                                onClick={() => handleDeleteTool(sectionIdx, toolIdx)}
                                className="opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-300"
                              >
                                <X className="h-3 w-3" />
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                      
                      {/* Safety Protocols - Editable */}
                      <div className="p-5 border-b border-slate-700/50">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <Shield className="h-4 w-4 text-emerald-400" />
                            <p className="text-xs text-emerald-400 uppercase tracking-wider font-bold">SAFETY PROTOCOLS</p>
                          </div>
                          <button
                            onClick={() => handleAddSafetyProtocol(sectionIdx)}
                            className="flex items-center gap-1 text-emerald-400 hover:text-emerald-300 text-sm"
                          >
                            <Plus className="h-4 w-4" />
                            Add Protocol
                          </button>
                        </div>
                        <div className="space-y-2">
                          {section.safetyProtocols.map((protocol, protocolIdx) => (
                            <div key={protocolIdx} className="flex items-start gap-3 group">
                              <CheckCircle2 className="h-4 w-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                              <input
                                type="text"
                                value={protocol}
                                onChange={(e) => handleEditSafetyProtocol(sectionIdx, protocolIdx, e.target.value)}
                                className="text-slate-300 bg-transparent border-b border-transparent hover:border-emerald-500 focus:border-emerald-500 outline-none flex-1"
                              />
                              <button
                                onClick={() => handleDeleteSafetyProtocol(sectionIdx, protocolIdx)}
                                className="opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-300"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                      
                      {/* Task List */}
                      <div className="p-5 border-b border-slate-700/50">
                        <div className="flex items-center gap-2 mb-3">
                          <Clipboard className="h-4 w-4 text-blue-400" />
                          <p className="text-xs text-blue-400 uppercase tracking-wider font-bold">TASK EXECUTION LIST</p>
                        </div>
                        <div className="overflow-x-auto">
                          <table className="w-full">
                            <thead>
                              <tr className="text-left text-xs text-slate-500 uppercase">
                                <th className="pb-2 pr-4 w-12">#</th>
                                <th className="pb-2 pr-4">Task Description</th>
                                <th className="pb-2 pr-4 w-20 text-center">QTY</th>
                                <th className="pb-2 pr-4 w-16 text-center">Unit</th>
                                <th className="pb-2 w-20 text-center">Status</th>
                              </tr>
                            </thead>
                            <tbody>
                              {section.items.map((item, itemIdx) => (
                                <tr key={item.id} className="border-t border-slate-700/30">
                                  <td className="py-3 pr-4 text-slate-500 text-sm">{itemIdx + 1}</td>
                                  <td className="py-3 pr-4 text-white font-medium">{item.name}</td>
                                  <td className="py-3 pr-4 text-center text-slate-400">{item.quantity}</td>
                                  <td className="py-3 pr-4 text-center text-slate-500">{item.unit}</td>
                                  <td className="py-3 text-center">
                                    <span className="text-xs px-2 py-1 rounded" style={{ backgroundColor: "rgba(100, 116, 139, 0.2)", color: "#94a3b8" }}>
                                      {item.status}
                                    </span>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                      
                      {/* QC Checklist - Editable */}
                      <div className="p-5">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <Eye className="h-4 w-4 text-purple-400" />
                            <p className="text-xs text-purple-400 uppercase tracking-wider font-bold">QC VERIFICATION CHECKLIST</p>
                          </div>
                          <button
                            onClick={() => handleAddQcItem(sectionIdx)}
                            className="flex items-center gap-1 text-purple-400 hover:text-purple-300 text-sm"
                          >
                            <Plus className="h-4 w-4" />
                            Add Item
                          </button>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          {section.qcChecklist.map((item, itemIdx) => (
                            <div key={itemIdx} className="flex items-start gap-3 group">
                              <div 
                                className="w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 mt-0.5"
                                style={{ borderColor: "rgba(168, 85, 247, 0.5)" }}
                              >
                                <span className="text-xs text-purple-400">☐</span>
                              </div>
                              <input
                                type="text"
                                value={item.text}
                                onChange={(e) => handleEditQcItem(sectionIdx, itemIdx, e.target.value)}
                                className="text-slate-300 text-sm bg-transparent border-b border-transparent hover:border-purple-500 focus:border-purple-500 outline-none flex-1"
                              />
                              <button
                                onClick={() => handleDeleteQcItem(sectionIdx, itemIdx)}
                                className="opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-300"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* ═══════════════════════════════════════════════════════════════════════════
            SECTION 6: POST-MISSION REQUIREMENTS - Editable
            ═══════════════════════════════════════════════════════════════════════════ */}
        <div className="px-10 pb-8">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-emerald-400" />
              <p className="text-xs text-slate-500 uppercase tracking-widest">POST-MISSION REQUIREMENTS</p>
            </div>
            <button
              onClick={() => handleAddChecklistItem('post')}
              className="flex items-center gap-1 text-emerald-400 hover:text-emerald-300 text-sm"
            >
              <Plus className="h-4 w-4" />
              Add Item
            </button>
          </div>
          
          <div 
            className="p-5 rounded-lg"
            style={{ backgroundColor: "rgba(16, 185, 129, 0.1)", border: "1px solid rgba(16, 185, 129, 0.3)" }}
          >
            <div className="grid grid-cols-2 gap-3">
              {postMissionChecklist.map((item, idx) => (
                <div key={item.id} className="flex items-center gap-3 group">
                  <div className="w-5 h-5 rounded border-2 border-emerald-500/50 flex items-center justify-center flex-shrink-0">
                    <span className="text-xs text-emerald-400">☐</span>
                  </div>
                  <input
                    type="text"
                    value={item.text}
                    onChange={(e) => handleEditChecklistItem('post', idx, e.target.value)}
                    className="text-slate-300 bg-transparent border-b border-transparent hover:border-emerald-500 focus:border-emerald-500 outline-none flex-1"
                  />
                  <button
                    onClick={() => handleDeleteChecklistItem('post', idx)}
                    className="opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-300"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ═══════════════════════════════════════════════════════════════════════════
            SECTION 7: AUTHORIZATION
            ═══════════════════════════════════════════════════════════════════════════ */}
        <div className="px-10 pb-10">
          <div className="flex items-center gap-2 mb-4">
            <FileText className="h-4 w-4 text-slate-400" />
            <p className="text-xs text-slate-500 uppercase tracking-widest">OPERATOR AUTHORIZATION</p>
          </div>
          
          <div 
            className="p-6 rounded-lg"
            style={{ backgroundColor: "rgba(30, 41, 59, 0.3)", border: "1px solid rgba(100, 116, 139, 0.2)" }}
          >
            <div className="grid grid-cols-2 gap-8">
              <div>
                <p className="text-slate-500 text-xs uppercase tracking-wider mb-2">Lead Operator Signature</p>
                <div 
                  className="h-16 rounded mb-2 flex items-center justify-center"
                  style={{ backgroundColor: "rgba(15, 23, 42, 0.5)", border: "1px dashed rgba(100, 116, 139, 0.3)" }}
                >
                  <span className="text-slate-600 text-sm">Sign Here</span>
                </div>
                <p className="text-slate-400 text-sm">Print Name: _______________________</p>
              </div>
              <div>
                <p className="text-slate-500 text-xs uppercase tracking-wider mb-2">Date / Time</p>
                <div 
                  className="h-16 rounded mb-2 flex items-center justify-center"
                  style={{ backgroundColor: "rgba(15, 23, 42, 0.5)", border: "1px dashed rgba(100, 116, 139, 0.3)" }}
                >
                  <span className="text-slate-400">{formatDate()}</span>
                </div>
                <p className="text-slate-400 text-sm">Time In: _______ Time Out: _______</p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div 
          className="px-10 py-6 text-center"
          style={{
            borderTop: "1px solid rgba(100, 116, 139, 0.2)",
            backgroundColor: "rgba(15, 23, 42, 0.5)"
          }}
        >
          <p className="text-slate-500 text-xs">
            CLASSIFIED INTERNAL DOCUMENT - {companySettings?.company_name || "Alpha Construction Pros"}
          </p>
          <p className="text-slate-600 text-xs mt-1">
            Questions? Contact Operations at {companySettings?.phone || "(555) 123-4567"}
          </p>
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════════════════════════════
          FIXED BOTTOM ACTION BAR
          ═══════════════════════════════════════════════════════════════════════════ */}
      <div 
        className="fixed bottom-0 left-0 right-0 z-50 px-6 py-4"
        style={{ 
          backgroundColor: "rgba(15, 23, 42, 0.95)",
          borderTop: "1px solid rgba(59, 130, 246, 0.3)",
          backdropFilter: "blur(10px)"
        }}
      >
        <div className="max-w-[900px] mx-auto flex items-center justify-between">
          <button
            onClick={onClose}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-blue-400 hover:text-white hover:bg-slate-800 transition-all"
          >
            <ArrowLeft className="h-4 w-4" />
            <span className="font-medium">← Back</span>
          </button>
          
          <button
            onClick={() => setSendModalOpen(true)}
            className="flex items-center gap-2 px-8 py-3 rounded-lg font-bold text-white transition-all hover:scale-105"
            style={{ backgroundColor: "#3b82f6" }}
          >
            <Send className="h-5 w-5" />
            Send Operator Order
          </button>
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════════════════════════════
          SEND MODAL
          ═══════════════════════════════════════════════════════════════════════════ */}
      {sendModalOpen && (
        <div 
          className="fixed inset-0 z-[200] flex items-center justify-center"
          style={{ backgroundColor: "rgba(0, 0, 0, 0.8)" }}
          onClick={() => setSendModalOpen(false)}
        >
          <div 
            className="w-full max-w-md p-6 rounded-lg"
            style={{ backgroundColor: "#1e293b", border: "1px solid rgba(59, 130, 246, 0.3)" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-white font-bold text-lg">Send Operator Order</h3>
              <button 
                onClick={() => setSendModalOpen(false)}
                className="text-slate-400 hover:text-white"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="space-y-3">
              {/* Send via Email */}
              <button
                className="w-full py-4 px-5 rounded-lg text-left transition-all flex items-center justify-between hover:bg-slate-700"
                style={{
                  backgroundColor: "rgba(30, 41, 59, 0.5)",
                  border: "1px solid rgba(100, 116, 139, 0.3)"
                }}
              >
                <div className="flex items-center gap-3">
                  <Mail className="h-5 w-5 text-blue-400" />
                  <div>
                    <p className="text-white font-medium">Send via Email</p>
                    <p className="text-slate-500 text-sm">Email to crew member or subcontractor</p>
                  </div>
                </div>
              </button>
              
              {/* Send via SMS */}
              <button
                className="w-full py-4 px-5 rounded-lg text-left transition-all flex items-center justify-between hover:bg-slate-700"
                style={{
                  backgroundColor: "rgba(30, 41, 59, 0.5)",
                  border: "1px solid rgba(100, 116, 139, 0.3)"
                }}
              >
                <div className="flex items-center gap-3">
                  <MessageSquare className="h-5 w-5 text-emerald-400" />
                  <div>
                    <p className="text-white font-medium">Send via SMS</p>
                    <p className="text-slate-500 text-sm">Text to crew member</p>
                  </div>
                </div>
              </button>
              
              {/* Copy Link */}
              <button
                onClick={async () => {
                  if (!estimateId) {
                    toast.error("Please select an estimate first to generate a shareable link");
                    return;
                  }
                  if (!publicToken) {
                    toast.error("Please wait for document to be created");
                    return;
                  }
                  // Save before copying link to ensure data is up to date
                  await saveOrder();
                  const orderLink = `${window.location.origin}/operator-order/${publicToken}`;
                  try {
                    await navigator.clipboard.writeText(orderLink);
                    setLinkCopied(true);
                    toast.success("Link copied to clipboard!");
                    setTimeout(() => setLinkCopied(false), 3000);
                  } catch (error) {
                    toast.error("Failed to copy link");
                  }
                }}
                disabled={!estimateId || loading}
                className="w-full py-4 px-5 rounded-lg text-left transition-all flex items-center justify-between hover:bg-slate-700"
                style={{
                  backgroundColor: linkCopied ? "rgba(16, 185, 129, 0.15)" : "rgba(30, 41, 59, 0.5)",
                  border: linkCopied ? "1px solid rgba(16, 185, 129, 0.5)" : "1px solid rgba(100, 116, 139, 0.3)",
                  opacity: !estimateId || loading ? 0.6 : 1
                }}
              >
                <div className="flex items-center gap-3">
                  {linkCopied ? <Check className="h-5 w-5 text-emerald-400" /> : <Link2 className="h-5 w-5 text-amber-400" />}
                  <div>
                    <p className="text-white font-medium">
                      {!estimateId ? "Select Estimate First" : loading ? "Creating..." : !publicToken ? "Preparing..." : linkCopied ? "Link Copied!" : "Copy Link"}
                    </p>
                    <p className="text-slate-500 text-sm">
                      {!estimateId ? "An estimate must be selected" : "Share a direct link to this order"}
                    </p>
                  </div>
                </div>
              </button>
              
              {/* Create PDF */}
              <button
                className="w-full py-4 px-5 rounded-lg text-left transition-all flex items-center justify-between hover:bg-slate-700"
                style={{
                  backgroundColor: "rgba(30, 41, 59, 0.5)",
                  border: "1px solid rgba(100, 116, 139, 0.3)"
                }}
              >
                <div className="flex items-center gap-3">
                  <Download className="h-5 w-5 text-purple-400" />
                  <div>
                    <p className="text-white font-medium">Create PDF</p>
                    <p className="text-slate-500 text-sm">Download printable PDF version</p>
                  </div>
                </div>
              </button>
            </div>
            
            <button
              onClick={() => setSendModalOpen(false)}
              className="w-full mt-6 py-3 rounded-lg text-slate-400 hover:text-white transition-colors"
              style={{ backgroundColor: "rgba(30, 41, 59, 0.5)" }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default OperatorOrderBuilder;
